import { useEffect, useRef } from 'react';
import { useTheme } from '@sakhtar/design-system/react';
import { echarts, type ECharts, type EChartsCoreOption } from './echarts-setup.js';
import { resolveChartTheme, type ChartTheme } from './chartTheme.js';
import { sameInputs } from './chartInputs.js';

/** Every chart text (axes, legend, tooltip, labels) uses the design-system font, not ECharts' default. */
function withFont(option: EChartsCoreOption, theme: ChartTheme): EChartsCoreOption {
  return {
    ...option,
    textStyle: { fontFamily: theme.fontFamily, ...(option['textStyle'] as object | undefined) },
  };
}

/**
 * Owns one ECharts instance per mount: init once, resize via
 * ResizeObserver, dispose on unmount, and re-render `buildOption` whenever
 * the caller's `deps` change or the light/dark theme flips (re-reading
 * `--sk-*` off the container so a toggle repaints with real colors, not a
 * stale snapshot). Callers never touch the echarts instance directly.
 *
 * A full repaint (`notMerge`) recreates every series and replays the entry
 * animation (the line draws in from the left, bars grow from zero). It must
 * therefore only happen when the chart's inputs really changed: a parent that
 * re-renders for an unrelated reason (hover, focus, a timer) while handing
 * over equal data used to restart that animation on every render, so the
 * chart looked hidden for as long as the pointer kept moving over the page.
 */
export function useEChart(
  buildOption: (theme: ChartTheme) => EChartsCoreOption,
  deps: readonly unknown[],
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const buildOptionRef = useRef(buildOption);
  buildOptionRef.current = buildOption;
  const { mode } = useTheme();
  const painted = useRef<{ mode: string; deps: readonly unknown[] } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const chart = echarts.init(el);
    chartRef.current = chart;
    const paint = (notMerge: boolean) => {
      const theme = resolveChartTheme(el);
      chart.setOption(withFont(buildOptionRef.current(theme), theme), notMerge);
    };
    paint(false);
    painted.current = { mode, deps };
    // Canvas text is drawn once: repaint when the web font finishes loading.
    let disposed = false;
    void document.fonts?.ready.then(() => {
      if (!disposed) paint(true);
    });

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(el);

    return () => {
      disposed = true;
      resizeObserver.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
    // Instance lifecycle only — data/theme updates go through the effect below.
  }, []);

  // Runs after every render but only repaints when the inputs really changed.
  useEffect(() => {
    const el = containerRef.current;
    const chart = chartRef.current;
    if (!el || !chart) return;
    const last = painted.current;
    if (last && last.mode === mode && sameInputs(last.deps, deps)) return;
    const theme = resolveChartTheme(el);
    chart.setOption(withFont(buildOptionRef.current(theme), theme), true);
    painted.current = { mode, deps };
    // `deps` is caller-controlled and stable in length per call site.
  });

  return containerRef;
}
